import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpService } from '../service/http.service';
import { SchoolInvoiceService } from '../school-invoice.service';
import * as moment from 'moment';
import { DecimalValidator } from '../validator/decimal-validator';
import { BookingService } from '../service/booking.service';
import { SchoolInvoice } from '../school-invoice';
import { ToastMessageService } from '../toast-message.service';
import { SchoolInvoiceWithPaymentOrders } from '../school-invoice-with-payment-orders';
import { PaymentOrder } from '../payment-order';
import { NumberValidator } from '../validator/number-validator';
import { InvoiceFormValidator } from '../validator/invoice-form-validator';
import { CurrencyService } from '../service/currency.service';
import { BookingTransactionsService } from '../service/booking-transactions.service';
import { ConfirmationService } from 'primeng/api';
import Decimal from 'decimal.js';
import { UserService } from '../service/user.service';
import { FileUpload } from 'primeng/fileupload';
import { SchoolInvoicePost } from '../school-invoice-post';
@Component({
  selector: 'app-create-school-invoice',
  templateUrl: './create-school-invoice.component.html',
  styleUrls: ['./create-school-invoice.component.css']
})
export class CreateSchoolInvoiceComponent implements OnInit {

  invoiceForm!: FormGroup;
  paymentOrderForms: FormGroup[] = [];
  submitFail = false;
  invalidAmount = false;
  showPaymentTypeDialog = false;
  isSchoolInvoiceValidator: boolean = false;
  selectedFile: File | null = null;

  @Output() newInvoiceEvent = new EventEmitter<SchoolInvoice | null>();
  @ViewChild("fileUploader") fileUploader: FileUpload | null = null;

  constructor(
    private httpService: HttpService,
    public schoolInvoiceService: SchoolInvoiceService,
    private builder: FormBuilder,
    public bookingService: BookingService,
    private toastService: ToastMessageService,
    public currencyService: CurrencyService,
    private bookingTransactionService: BookingTransactionsService,
    private confirmService: ConfirmationService,
    private userService: UserService
  ) {
    if(userService.hasAnyRole(["superadmin", "school_invoice_validator"])){
      this.isSchoolInvoiceValidator = true;
    }
   }

  ngOnInit(): void {
    this.invoiceForm = this.builder.group({
      invoiceNumber: null,
      amount: [0, Validators.compose([DecimalValidator, NumberValidator])],
      currency: this.currencyService.currencies[0],
      date: [new Date(), Validators.required],
      dueDate: null,
      paymentType: [this.bookingService.paymentTypes[0], Validators.required],
      comment: null,
      multiplePayments: false,
      isValid: false
    }, {
      validator: InvoiceFormValidator
    });
    this.addPaymentOrder(2);
  }

  addPaymentOrder(addNum: number){
    for(let i = 1; i <= addNum; i++){
      this.paymentOrderForms.push(this.builder.group({
        amount: [0, Validators.compose([DecimalValidator, NumberValidator])],
        dueDate: [null, Validators.required]
      }));
    }
  }

  amountChange(event: any){
    let value: string = event.target.value as string;
    if(value.includes(",")){
      value = value.replaceAll(",", ".");
      event.target.value = value;
    }
  }

  onFileSelect(files: File[]){
    if(files.length > 0){
      this.selectedFile = files[0];
    }
  }
  clearSelectedFile(){
    this.fileUploader?.clear();
    this.selectedFile = null;
  }

  uploadSchoolInvoiceFile(files: File[], form: any){
    if (files.length == 0) {
      return;
    }
  }

  deletePaymentOrderForm(i: number){
    this.paymentOrderForms.splice(i, 1);
  }

  submit(){
    if(!this.selectedFile){
      this.toastService.addError("Error", "You need to upload the invoice file");
      return;
    }
    this.submitFail = false;
    if(this.invoiceForm.invalid){
      this.toastService.addError("Error", "Form is not completed correctly.");
      this.submitFail = true;
      return;
    }
    let paymentTypeId = this.bookingTransactionService.getPaymentReceivedType();
    let paymentType = this.invoiceForm.value.paymentType;
    if(paymentTypeId != null && paymentType.id != paymentTypeId){
      this.showPaymentTypeDialog = true;
      this.confirmService.confirm({
        message: "Means of payment: \"" + paymentType.name + "\" does not match the means of payment of received payments from customer. Are you sure to create the invoice with this configuration?",
        accept: () => {
          this.postNewInvoice();
        },
        reject: () => {
          this.submitFail = true;
          return;
        }
      });
    } else {
      this.postNewInvoice();
    }
  }

  toUSFormatAmount(amountStr: string){
    return amountStr.replace(",", ".");
  }

  postNewInvoice(){
    if(!this.invoiceForm.value.multiplePayments){
      let amountStr = this.toUSFormatAmount(this.invoiceForm.value.amount);
      let invoice = new SchoolInvoicePost(
        this.invoiceForm.value.invoiceNumber,
        moment(this.invoiceForm.value.date).utc(true),
        new Decimal(amountStr),
        this.invoiceForm.value.currency,
        this.invoiceForm.value.paymentType,
        this.invoiceForm.value.comment,
        moment(this.invoiceForm.value.dueDate),
        this.bookingService.booking.id!,
        this.invoiceForm.value.isValid
      );
      this.httpService.postNewSchoolInvoice(invoice, this.selectedFile!, this.bookingService.booking.id!).subscribe(res => {
        this.toastService.addSuccess("Success", "Invoice added");
        this.newInvoiceEvent.emit(null);
      }, error=> {
        this.toastService.addError("Error", error.message);
        this.submitFail = true;
      });
    } else {
      if(!this.schoolInvoiceService.checkPaymentAmounts(this.invoiceForm, this.paymentOrderForms)){
        this.invalidAmount = true;
        return;
      } else {
        this.invalidAmount = false;
      }
      if(this.paymentOrderForms.length == 0){
        this.toastService.addError("Error", "You must make at least one payment order");
        throw new Error("You need to make at least one payment order");
      }
      let amountStr = this.toUSFormatAmount(this.invoiceForm.value.amount);
      let paymentOrders: PaymentOrder[] = [];
      let invoice = new SchoolInvoiceWithPaymentOrders(
        this.invoiceForm.value.invoiceNumber,
        moment(this.invoiceForm.value.date).utc(true),
        new Decimal(amountStr),
        this.invoiceForm.value.currency,
        this.invoiceForm.value.paymentType,
        this.invoiceForm.value.comment,
        null,
        paymentOrders,
        this.invoiceForm.value.isValid
      );
        for(let order of this.paymentOrderForms){
          if(order.valid){
            paymentOrders.push(new PaymentOrder(
              order.value.amount,
              null,
              moment(order.value.dueDate).utc(true),
              invoice.currency
            ));
          } else {
            this.toastService.addError("Error", "Form value is invalid");
            throw new Error("there is invalid order form.");
          }
        }
        this.httpService.postSchoolInvoiceWithPaymentOrders(invoice, this.selectedFile!, this.bookingService.booking.id!).subscribe(() => {
          this.toastService.addSuccess("Success", "Invoice successfully added.");
          this.newInvoiceEvent.emit(null);
        }, error => {
          this.submitFail = true;
          this.toastService.addError("Error", error.message);
        });
    }
  }

}
